/**
 * ConnectedApplications — list and revoke application grants.
 *
 * Spec section 3, requirement 5: "the user can list and revoke application
 * grants from their account settings, per application, without signing out
 * anywhere else." Revocation is the only control a user has after consenting,
 * which makes this screen the other half of the consent screen rather than a
 * nice-to-have.
 *
 * Revoking is immediate: the application's access token stops working on its
 * very next request, not at its next expiry (the server validates by looking the
 * grant up, which is why the token is opaque rather than self-describing).
 */

import { useCallback, useEffect, useState } from 'react';
import { ShieldOff, Loader2 } from 'lucide-react';
import { listGrants, revokeGrant, type AccountGrant } from '../../services/grantsApi';

export function ConnectedApplications(): JSX.Element {
  const [grants, setGrants] = useState<AccountGrant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setGrants(await listGrants());
      setError(null);
    } catch {
      setError('Could not load your connected applications.');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const revoke = useCallback(
    async (grant: AccountGrant) => {
      setRevoking(grant.grant_id);
      try {
        await revokeGrant(grant.grant_id);
        await reload();
      } catch {
        setError(`Could not revoke ${grant.display_name}. Please try again.`);
      } finally {
        setRevoking(null);
      }
    },
    [reload]
  );

  return (
    <section>
      <h2 className="text-lg font-semibold text-white">Connected applications</h2>
      <p className="mt-1 text-sm text-gray-400">
        Applications you have allowed to use your account. Removing one stops it immediately and
        does not sign you out anywhere.
      </p>

      {error && (
        <p className="mt-4 rounded-lg border border-red-800/60 bg-red-950/30 p-3 text-sm text-red-300">
          {error}
        </p>
      )}

      {grants === null && !error && (
        <div className="mt-6 flex items-center gap-2 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      )}

      {grants !== null && grants.length === 0 && (
        <p className="mt-6 text-sm text-gray-500">No applications are connected to your account.</p>
      )}

      <ul className="mt-6 flex flex-col gap-3">
        {(grants ?? []).map((grant) => (
          <li
            key={grant.grant_id}
            className="flex flex-col gap-3 rounded-lg border border-gray-800 bg-gray-900/40 p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="truncate font-medium text-white">{grant.display_name}</p>
              {grant.publisher && (
                <p className="truncate text-xs text-gray-500">by {grant.publisher}</p>
              )}
              <p className="mt-2 text-xs text-gray-400">Can: {grant.scopes.join(', ')}</p>
              <p className="mt-1 text-xs text-gray-600">
                {grant.last_used_at
                  ? `Last used ${new Date(grant.last_used_at * 1000).toLocaleString()}`
                  : 'Not used yet'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void revoke(grant)}
              disabled={revoking === grant.grant_id}
              className="flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-red-800/70 px-4 py-2 text-sm font-medium text-red-300 hover:bg-red-950/40 disabled:opacity-50"
            >
              {revoking === grant.grant_id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ShieldOff className="h-4 w-4" />
              )}
              Remove access
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
